import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Card,
  Spinner,
  Tag,
  Text,
  Title,
  Tooltip,
} from '@nimbus-ds/components';
import { NotificationDto } from '@tiendanube/common';
import FeatureFlag from 'App/components/FeatureFlag';
import { NEW_ADMIN_WHATSAPP_NOTIFICATIONS } from 'App/featuresFlags';
import Toggle from 'commons/components/Toggle';
import { useTranslationLanguage } from 'commons/hooks';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import { WhatsAppBanner } from './WhatsAppBanner';
import { WhatsAppInitialScreen } from './WhatsAppInitialScreen';
import { WhatsAppPreview } from './WhatsAppPreview';
import {
  useGetWhatsappNotificationByIdQuery,
  useGetWhatsappNotificationSettingsQuery,
  useUpdateWhatsappNotificationByIdMutation,
} from '../../../notificationsApi';

const getTemplateForCurrentLanguage = (
  whatsappNotification: NotificationDto | undefined,
  currentLanguage: string,
) => {
  const templates = whatsappNotification?.whatsapp?.templates;
  if (!templates) return null;

  const templateKey = currentLanguage.replace('-', '_');

  return templates[templateKey] || Object.values(templates)[0] || null;
};

export function WhatsappNotificationTab({
  notificationId,
  isEnabled,
  emailTitle,
}: {
  notificationId: string;
  isEnabled: boolean;
  emailTitle: string;
}) {
  const t = useTranslationConfigurations('notifications.whatsapp');
  const currentLanguage = useTranslationLanguage();

  const [enabled, setEnabled] = useState(isEnabled);

  const [updateWhatsappNotification, { isLoading }] =
    useUpdateWhatsappNotificationByIdMutation();
  const { data: settings, isLoading: isLoadingSettings } =
    useGetWhatsappNotificationSettingsQuery();
  const { data: whatsappNotification, isLoading: isLoadingNotification } =
    useGetWhatsappNotificationByIdQuery(notificationId);

  useEffect(() => {
    if (whatsappNotification?.whatsapp?.enabled !== undefined) {
      setEnabled(whatsappNotification.whatsapp.enabled);
    } else {
      setEnabled(isEnabled);
    }
  }, [whatsappNotification, isEnabled]);

  const currentTemplate = getTemplateForCurrentLanguage(
    whatsappNotification,
    currentLanguage,
  );

  const handleToggleChange = async () => {
    if (!whatsappNotification) {
      return;
    }

    const newState = !enabled;
    setEnabled(newState);

    try {
      await updateWhatsappNotification({
        id: notificationId,
        payload: {
          whatsapp: {
            available: whatsappNotification.whatsapp.available,
            enabled: newState,
          },
        },
      }).unwrap();
    } catch (error) {
      setEnabled(!newState);
      console.error(error);
    }
  };

  if (isLoadingSettings || isLoadingNotification) {
    return (
      <Box
        width="100%"
        height="200px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Box>
    );
  }

  if (!settings || !settings.whatsapp.is_connected) {
    return <WhatsAppInitialScreen />;
  }

  return (
    <Box
      width="100%"
      height="auto"
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      justifyContent="flex-start"
      gap="4"
    >
      <Card>
        <Card.Body>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="flex-start"
              gap="2"
            >
              <Title as="h4">{t('whatsappNotification.sendByWhatsApp')}</Title>
              <Tag appearance={enabled ? 'success' : 'neutral'}>
                <Text color={enabled ? 'success-textLow' : 'neutral-textLow'}>
                  {enabled
                    ? t('whatsappNotification.enabled')
                    : t('whatsappNotification.disabled')}
                </Text>
              </Tag>
            </Box>

            <Tooltip content={t('whatsappNotification.toggleTooltip')}>
              <Toggle
                name="enable-whatsapp"
                checked={enabled}
                onChange={handleToggleChange}
                disabled={isLoading}
              />
            </Tooltip>
          </Box>
        </Card.Body>
      </Card>

      <FeatureFlag flag={NEW_ADMIN_WHATSAPP_NOTIFICATIONS}>
        {(!isLoadingNotification || !isLoadingSettings) && enabled && (
          <WhatsAppBanner limitedActivityNotice />
        )}
      </FeatureFlag>

      {isLoading && (
        <Box
          width="100%"
          height="200px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Spinner />
        </Box>
      )}

      {enabled && !isLoading && (
        <>
          {currentTemplate && currentTemplate.status !== 'APPROVED' ? (
            <Alert appearance="warning" title="">
              {t('whatsappNotification.pendingApprovalDescription')}
            </Alert>
          ) : null}

          <Card>
            <Card.Header>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Title as="h4">{t('whatsappNotification.templateTitle')}</Title>
              </Box>
            </Card.Header>

            <Card.Body>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                justifyContent="flex-start"
                gap="2"
              >
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="flex-start"
                  gap="2"
                >
                  <Text color="neutral-textDisabled">
                    {t('whatsappNotification.previewText')} {emailTitle}
                  </Text>

                  <Tag
                    appearance={
                      currentTemplate?.status === 'APPROVED'
                        ? 'success'
                        : 'neutral'
                    }
                  >
                    <Text
                      color={
                        currentTemplate?.status === 'APPROVED'
                          ? 'success-textLow'
                          : 'neutral-textLow'
                      }
                    >
                      {currentTemplate?.status === 'APPROVED'
                        ? t('whatsappNotification.approvedByMeta')
                        : t('whatsappNotification.pendingApproval')}
                    </Text>
                  </Tag>
                </Box>

                <Box
                  width="100%"
                  height="auto"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  alignSelf="center"
                  backgroundColor="neutral-surface"
                  position="relative"
                  overflow="hidden"
                  borderRadius="base"
                  borderColor="neutral-interactive"
                  borderWidth="1"
                  borderStyle="solid"
                  paddingY="4"
                >
                  {currentTemplate && (
                    <WhatsAppPreview templateId={currentTemplate.template_id} />
                  )}
                </Box>
              </Box>
            </Card.Body>
          </Card>
        </>
      )}
    </Box>
  );
}
